import { Box, Button, Popper, ClickAwayListener, Stack, styled, Typography, Link, Fade } from "@mui/material";
import React, { useCallback } from "react";
import { TutorialContext } from "../../Utils/Helpers/tutorial";
import { useJoyride } from "../../Utils/Hooks/useJoyride";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import TaskOutlinedIcon from "@mui/icons-material/TaskOutlined";
import Shop2OutlinedIcon from "@mui/icons-material/Shop2Outlined";
import CopyrightRoundedIcon from "@mui/icons-material/CopyrightRounded";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import NotificationsIcon from "@mui/icons-material/NotificationsNone";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import FilePresentIcon from "@mui/icons-material/FilePresent";
import BugReportIcon from "@mui/icons-material/BugReport";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import { cacheService, EnvType, supportService, userService } from "../../Core/Api/services";
import { StartupHelpTutorialType } from "../../Core/Api/Types/startup-help-tutorial-type";
import useCustomSnackbar from "../../Utils/Hooks/useCustomSnackbar";
import { routePaths } from "../../Router/router";
import { useNavigate } from "react-router";
import { OpenLinkInNewTab, UserHasAcl } from "../../Utils/helpers";
import { AclTypes } from "../../Core/Api/Types/acl-types";
import { ModalContext } from "../Modals";
import { useDispatch, useSelector } from "react-redux";
import { IsShowDemo, SetShowDemo } from "../../Redux/Reducers/redux-slice-show-demo";
import ModalSupportZendeskTicket, {
  ModalSupportZendeskTicketProps,
} from "../Modals/ModalSupportTicket/modal-support-ticket-zendesk";
import GlobalConst from "../../Core/Utils/Constants/globals";
import { ZendeskTokenContext } from "../../Core/Api/Types/zendesk-token-context";
import { GetTokenRequest } from "../../Core/Api/Types/get-token-request";
import { useTranslation } from "react-i18next";

export interface HelpPopupProps {
  anchorEl: HTMLElement | null;
  visible: boolean;
  onClose: () => void;
}

const HelpPopup: React.FC<HelpPopupProps> = ({ anchorEl, visible, onClose }) => {
  const { t } = useTranslation();
  const demoTutorialJoyride = React.useMemo(
    () => [
      {
        target: "#tutorial-uikit-demo-dashboard",
        _tutorialTitle: "demoTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.stayUpToDate"),
        titleIcon: <DashboardOutlinedIcon />,
        content: t("component:joyRide.content.stayUpToDate"),
      },
      {
        target: "#tutorial-uikit-demo-catalog",
        _tutorialTitle: "demoTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.browseUpTo", { countInString: "10,000" }),
        titleIcon: <SlideshowIcon />,
        content: t("component:joyRide.content.findAllContent"),
      },
      {
        target: "#tutorial-uikit-demo-inventory",
        _tutorialTitle: "demoTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.titlesAssetsShares"),
        titleIcon: <Inventory2OutlinedIcon />,
        content: t("component:joyRide.content.searchBrowseFind"),
      },
      {
        target: "#tutorial-uikit-demo-collections",
        _tutorialTitle: "demoTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.createCollections"),
        titleIcon: <BookmarkBorderIcon />,
        content: t("component:joyRide.content.createCollectionsShowcase"),
      },
      {
        target: "#tutorial-uikit-demo-ingests",
        _tutorialTitle: "demoTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.trackYourIngests"),
        titleIcon: <TaskOutlinedIcon />,
        content: t("component:joyRide.content.trackStatusIngests"),
      },
      {
        target: "#tutorial-uikit-demo-orders",
        _tutorialTitle: "demoTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.trackYourOrders"),
        titleIcon: <Shop2OutlinedIcon />,
        content: t("component:joyRide.content.trackStatusOrders"),
      },
      {
        target: "#tutorial-uikit-demo-contracts",
        _tutorialTitle: "demoTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.findContracts"),
        titleIcon: <CopyrightRoundedIcon />,
        content: t("component:joyRide.content.browseContractsRights"),
      },
      {
        target: "#tutorial-uikit-demo-customers",
        _tutorialTitle: "demoTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.manageCustomers"),
        titleIcon: <GroupOutlinedIcon />,
        content: t("component:joyRide.content.createCustomerAccounts"),
      },
      {
        target: "#tutorial-uikit-demo-providers",
        _tutorialTitle: "demoTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.manageProviders"),
        titleIcon: <ManageAccountsOutlinedIcon />,
        content: t("component:joyRide.content.browseCreateProviders"),
      },
      {
        target: "#tutorial-uikit-demo-storefront",
        _tutorialTitle: "demoTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.configurePlatform"),
        titleIcon: <StorefrontOutlinedIcon />,
        content: t("component:joyRide.content.customizeHighlightTitles"),
      },
      {
        target: "#tutorial-uikit-demo-settings",
        _tutorialTitle: "demoTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.configurePlatformSettings"),
        titleIcon: <SettingsOutlinedIcon />,
        content: t("component:joyRide.content.manageUsersGroups"),
      },
      {
        target: "#tutorial-uikit-demo-profile",
        _tutorialTitle: "demoTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.userSettings"),
        titleIcon: <AccountCircleOutlinedIcon />,
        content: t("component:joyRide.content.changePasswordProfile"),
      },
      {
        target: "#tutorial-uikit-demo-notifications",
        _tutorialTitle: "demoTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.findNotifications"),
        titleIcon: <NotificationsIcon />,
        content: t("component:joyRide.content.reviewNotifications"),
      },
      {
        target: "#tutorial-uikit-demo-orderPanel",
        _tutorialTitle: "demoTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.findBasket"),
        titleIcon: <ShoppingBagOutlinedIcon />,
        content: t("component:joyRide.content.addToBasketBeforeConfig"),
      },
    ],
    []
  );

  const requestTutorialJoyride = React.useMemo(
    () => [
      {
        target: "#tutorial-request-pack",
        _tutorialTitle: "requestTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.configurationPack"),
        content: t("component:joyRide.content.selectConfigPack"),
      },
      {
        target: "#tutorial-request-groups",
        _tutorialTitle: "requestTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.findMetadata"),
        content: t("component:joyRide.content.groupMetadataIdentified"),
      },
      {
        target: "#tutorial-request-missing",
        _tutorialTitle: "requestTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.identifyQuickly"),
        content: t("component:joyRide.content.numberOfMandatory"),
      },
      {
        target: "#tutorial-request-menu",
        _tutorialTitle: "requestTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.copyUpdateReset"),
        content: t("component:joyRide.content.usePopupMenu"),
      },
      {
        target: "#tutorial-request-columns",
        _tutorialTitle: "requestTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.filterMetadata"),
        content: t("component:joyRide.content.filterMetadataDisplay"),
      },
      {
        target: "#tutorial-request-placeholders",
        _tutorialTitle: "requestTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.addPlaceholders"),
        content: t("component:joyRide.content.browseSelectPlaceholders"),
      },
      {
        target: "#tutorial-request-new",
        _tutorialTitle: "requestTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.createRequest"),
        content: t("component:joyRide.content.fillMandatoryMetadata"),
      },
    ],
    []
  );

  const markersTutorialJoyride = React.useMemo(
    () => [
      {
        target: "#tutorial-markers-set-demo",
        _tutorialTitle: "markersTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.adjustTimeframe"),
        content: t("component:joyRide.content.adjustFromPlayer"),
      },
    ],
    []
  );

  const ingestTutorialJoyride = React.useMemo(
    () => [
      {
        target: "#tutorial-ingest-processing",
        switch: true,
        to: routePaths.ingest.path,
        _tutorialTitle: "ingestTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.monitorIngests"),
        content: t("component:joyRide.content.allProcessingIngests"),
      },
      {
        target: "#tutorial-ingest-processing-menu",
        _tutorialTitle: "ingestTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.changePriority"),
        content: t("component:joyRide.content.useContextualMenu"),
      },
      {
        target: "#tutorial-ingest-requested",
        switch: true,
        to: routePaths.ingestRequested.path,
        _tutorialTitle: "ingestTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.assignedUploads"),
        content: t("component:joyRide.content.requestsAssignedUpload"),
      },

      {
        target: "#tutorial-ingest-request-upload",
        _tutorialTitle: "ingestTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.uploadMaterial"),
        content: t("component:joyRide.content.clickUploadButton"),
      },
      {
        target: "#tutorial-ingest-request-menu",
        _tutorialTitle: "ingestTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.modifyRequest"),
        content: t("component:joyRide.content.contextualMenuRequest"),
      },
      {
        target: "#tutorial-ingest-rejected",
        switch: true,
        to: routePaths.ingestRejected.path,
        _tutorialTitle: "ingestTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.manageRejections"),
        content: t("component:joyRide.content.actionRejectedIngests"),
      },
      {
        target: "#tutorial-ingest-rejected-menu",
        _tutorialTitle: "ingestTutorial",
        placement: "right" as const,
        title: t("common:actions"),
        content: t("component:joyRide.content.bypassReassignEdit"),
      },
      {
        target: "#tutorial-ingest-packs",
        switch: true,
        to: routePaths.ingestPacks.path,
        _tutorialTitle: "ingestTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.manageRequestPacks"),
        content: t("component:joyRide.content.managePackPlaceholder"),
      },
      {
        target: "#tutorial-ingest-column-set",
        switch: true,
        to: routePaths.ingestConfigureColset.path,
        _tutorialTitle: "ingestTutorial",
        placement: "right" as const,
        title: t("component:joyRide.title.manageMetadataSet"),
        content: t("component:joyRide.content.editAvailableMetadata"),
      },
      {
        target: "#tutorial-ingest-create-column",
        _tutorialTitle: "ingestTutorial",
        placement: "right" as const,
        title: t("button:addMetadata"),
        content: t("component:joyRide.content.useCreateColumn"),
      },
    ],
    []
  );

  const [RequestTutorial, joyRideRequestActions, joyRideRequestState] = useJoyride(requestTutorialJoyride);
  const [DemoTutorial, joyRideDemoActions, joyRideDemoState] = useJoyride(demoTutorialJoyride);
  const [markersTutorial, joyRideMarkersActions, joyRideMarkersState] = useJoyride(markersTutorialJoyride);
  const [ingestTutorial, joyRideIngestActions, joyRideIngestState] = useJoyride(ingestTutorialJoyride);

  const tutorialContext = React.useContext(TutorialContext);
  const { tutorialPage, updateTutorialState } = { ...tutorialContext };
  const [tipsVisible, setTipsVisible] = React.useState(visible);
  const [oneTimeDemo, setOneTimeDemo] = React.useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const showDemoFromRedux = useSelector(IsShowDemo);
  const showDemoOnLoginStartup = (cacheService
    .GetUserInfo()
    ?.startup?.tutorials?.includes(StartupHelpTutorialType.Home) ?? false) as boolean;

  React.useEffect(() => {
    if (showDemoFromRedux && showDemoOnLoginStartup && joyRideDemoActions) {
      setOneTimeDemo(true);
      joyRideDemoActions.start();
      dispatch(SetShowDemo(false));

      console.debug("Init demo started!");
    }
  }, [showDemoFromRedux, showDemoOnLoginStartup, joyRideDemoActions]);

  const handleDemoClick: React.MouseEventHandler = React.useCallback(() => {
    setTipsVisible(false);
    joyRideDemoActions.start();
  }, [joyRideDemoActions]);

  const handleRequestDemoClick: React.MouseEventHandler = React.useCallback(() => {
    setTipsVisible(false);
    joyRideRequestActions.start();
  }, [joyRideRequestActions]);

  const handleMarkersDemoClick: React.MouseEventHandler = React.useCallback(() => {
    setTipsVisible(false);
    joyRideMarkersActions.start();
  }, [joyRideMarkersActions]);

  const handleIngestDemoClick: React.MouseEventHandler = React.useCallback(() => {
    setTipsVisible(false);
    joyRideIngestActions.start();
  }, [joyRideIngestActions]);

  // Legacy tutorial
  React.useEffect(() => {
    if (!updateTutorialState) return;

    if (tutorialPage === "request") {
      updateTutorialState(joyRideRequestState.tutorialTitle, joyRideRequestState.stepIndex);
      return;
    }
    if (tutorialPage === "demo") {
      updateTutorialState(joyRideDemoState.tutorialTitle, joyRideDemoState.stepIndex);
      return;
    }
    if (tutorialPage === "markers") {
      updateTutorialState(joyRideMarkersState.tutorialTitle, joyRideMarkersState.stepIndex);
      return;
    }
    if (tutorialPage === "ingest") {
      updateTutorialState(joyRideIngestState.tutorialTitle, joyRideIngestState.stepIndex);
      return;
    }
  }, [
    updateTutorialState,
    joyRideRequestState.tutorialTitle,
    joyRideRequestState.stepIndex,
    joyRideDemoState.tutorialTitle,
    joyRideDemoState.stepIndex,
    joyRideMarkersState.tutorialTitle,
    joyRideMarkersState.stepIndex,
    joyRideIngestState.tutorialTitle,
    joyRideIngestState.stepIndex,
    tutorialPage,
  ]);

  // Legacy tutorial
  React.useEffect(() => {
    if (tutorialPage === "request" && !joyRideRequestState.isRunning && !tipsVisible) {
      onClose();
      return;
    }
    if (tutorialPage === "demo" && !joyRideDemoState.isRunning && !tipsVisible) {
      if (joyRideDemoState.stepIndex + 1 === demoTutorialJoyride.length && oneTimeDemo) {
        setOneTimeDemo(false);
        hideForever("demo");
      }
      onClose();
      return;
    }
    if (tutorialPage === "player" && !joyRideMarkersState.isRunning && !tipsVisible) {
      onClose();
      return;
    }
    if (tutorialPage === "ingest" && !joyRideIngestState.isRunning && !tipsVisible) {
      onClose();
      return;
    }
  }, [
    joyRideRequestState.isRunning,
    joyRideDemoState.isRunning,
    joyRideMarkersState.isRunning,
    joyRideIngestState.isRunning,
    tipsVisible,
    onClose,
    tutorialPage,
  ]);

  const enqueueCustomSnackbar = useCustomSnackbar();

  const hideForever = (tutorial: "request" | "demo" | "player" | "ingest") => {
    let user = cacheService.GetUserInfo();

    switch (tutorial) {
      case "request":
        if (user?.startup?.tutorials?.includes(StartupHelpTutorialType.IngestNewRequest)) {
          // update user
          const index = user?.startup?.tutorials.indexOf(StartupHelpTutorialType.IngestNewRequest);
          if (index > -1) {
            user?.startup?.tutorials.splice(index, 1);
          }
        }
        break;
      case "demo":
        if (user?.startup?.tutorials?.includes(StartupHelpTutorialType.Home)) {
          // update user
          const index = user?.startup?.tutorials.indexOf(StartupHelpTutorialType.Home);
          if (index > -1) {
            user?.startup?.tutorials.splice(index, 1);
          }
        }
        break;
      case "player":
        if (user?.startup?.tutorials?.includes(StartupHelpTutorialType.Markers)) {
          // update user
          const index = user?.startup?.tutorials.indexOf(StartupHelpTutorialType.Markers);
          if (index > -1) {
            user?.startup?.tutorials.splice(index, 1);
          }
        }
        break;
      case "ingest":
        if (user?.startup?.tutorials?.includes(StartupHelpTutorialType.Ingest)) {
          // update user
          const index = user?.startup?.tutorials.indexOf(StartupHelpTutorialType.Ingest);
          if (index > -1) {
            user?.startup?.tutorials.splice(index, 1);
          }
        }
        break;
    }

    // update user in database
    // update user in session
    userService.UpdateUser({ userInfos: user ?? null, userCustomersLinks: [] }).then(() => {
      enqueueCustomSnackbar(t("alert:success.userHelpSettingsUpdated"), "success");
      if (user) cacheService.SetUser(user);
    });
    onClose();
  };

  const goToDocuments = () => {
    onClose();
    navigate(routePaths.supportDocuments.path);
  };

  // Create zendesk ticket
  const handleCreateZendeskTicket = () => {
    if (!openModal) return;
    openModal<ModalSupportZendeskTicketProps>(ModalSupportZendeskTicket);
  };

  // User guiding
  const handleOpenUserHelpCenter = useCallback((redirectToRequests: boolean = false) => {
    let request: GetTokenRequest = {
      context: ZendeskTokenContext.HelpCenter,
    };
    supportService.GenerateZendeskToken(request).then((response) => {
      if (response.token) {
        let url = `${GlobalConst.ZENDESK_HELP_CENTER_URL}jwt?jwt=${response.token}`;
        if (redirectToRequests) url += `&return_to=${GlobalConst.ZENDESK_BASE_HC_URL}`;
        window.open(url, "_blank");
      } else enqueueCustomSnackbar(t("alert:error.creatingToken"), "error");
    });
  }, []);

  // Open zendesk chatbot
  const handleOpenZendeskChatbot: React.MouseEventHandler = React.useCallback(() => {
    let request: GetTokenRequest = {
      context: ZendeskTokenContext.Messaging,
    };
    supportService.GenerateZendeskToken(request).then((response) => {
      if (response.token) {
        if (window.zE) {
          window.zE("messenger", "loginUser", async function (callback) {
            callback(response.token!);
            window.zE!("messenger", "open");
          });
          console.info("ZD:: Zendesk chatbot open ok");
        } else {
          console.error("ZD:: Zendesk script is not loaded yet");
        }
      } else enqueueCustomSnackbar(t("alert:error.creatingToken"), "error");
    });
  }, []);

  return (
    <>
      {tutorialPage === "request" && visible && RequestTutorial}
      {tutorialPage === "demo" && DemoTutorial}
      {tutorialPage === "player" && visible && markersTutorial}
      {tutorialPage === "ingest" && visible && ingestTutorial}

      {tipsVisible && (
        <ClickAwayListener onClickAway={onClose}>
          <Popper
            id="help-popup"
            open={visible}
            anchorEl={anchorEl}
            transition
            sx={(theme) => ({ zIndex: theme.zIndex.drawer + 1 })}
            placement="right-end"
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={300}>
                <Wrapper visible={visible}>
                  <Stack direction="row" alignItems="center" justifyContent="center" mb={2.5} spacing={0.5}>
                    <HeadsetMicOutlinedIcon />
                    <Typography fontWeight={600} variant="subtitle2">
                      {t("component:body.difficultyOrHelp")}
                    </Typography>
                  </Stack>
                  <Stack alignItems="center" spacing={1.5}>
                    {tutorialPage === "request" && (
                      <>
                        <Button onClick={handleRequestDemoClick} variant="contained" fullWidth>
                          {t("button:viewTips")}
                        </Button>
                        <Button onClick={() => hideForever("request")} variant="white" fullWidth>
                          {t("button:notShowAnymore")}
                        </Button>
                      </>
                    )}
                    {tutorialPage === "demo" && (
                      <>
                        <Button onClick={handleDemoClick} variant="contained" fullWidth>
                          {t("button:viewTips")}
                        </Button>
                        {showDemoOnLoginStartup && !showDemoFromRedux && (
                          <Button onClick={() => hideForever("demo")} variant="white" fullWidth>
                            {t("button:notShowAnymore")}
                          </Button>
                        )}
                      </>
                    )}
                    {tutorialPage === "player" && (
                      <>
                        <Button onClick={handleMarkersDemoClick} variant="contained" fullWidth>
                          {t("button:viewTips")}
                        </Button>
                        <Button onClick={() => hideForever("player")} variant="white" fullWidth>
                          {t("button:notShowAnymore")}
                        </Button>
                      </>
                    )}
                    {tutorialPage === "ingest" && (
                      <>
                        <Button onClick={handleIngestDemoClick} variant="contained" fullWidth>
                          {t("button:viewTips")}
                        </Button>
                        <Button onClick={() => hideForever("ingest")} variant="white" fullWidth>
                          {t("button:notShowAnymore")}
                        </Button>
                      </>
                    )}
                    {GlobalConst.ZENDESK_ENABLED && (
                      <Button onClick={() => handleOpenUserHelpCenter()} variant="contained" fullWidth>
                        {t("button:openHelpCenter")}
                      </Button>
                    )}
                    {/*GlobalConst.ZENDESK_ENABLED && (
                      <Button onClick={handleOpenZendeskChatbot} variant="contained" fullWidth>
                        {t("button:askQuestion")}
                      </Button>
                    )*/}
                    {UserHasAcl(AclTypes.aclDocumentsAccess) && (
                      <Button onClick={goToDocuments} variant="white" fullWidth startIcon={<FilePresentIcon />}>
                        {t("common:usefulDocuments")}
                      </Button>
                    )}
                    {UserHasAcl(AclTypes.aclSupportTicketView) && GlobalConst.ZENDESK_ENABLED && (
                      <Button
                        onClick={() => handleOpenUserHelpCenter(true)}
                        variant="white"
                        fullWidth
                        startIcon={<BugReportIcon />}
                      >
                        {t("button:mySupportTickets")}
                      </Button>
                    )}

                    {UserHasAcl(AclTypes.aclSupportTicketCreate) && (
                      <Link
                        onClick={handleCreateZendeskTicket}
                        component="button"
                        sx={{
                          color: "common.white",
                          opacity: "0.5",
                        }}
                        underline="hover"
                      >
                        {t("button:contactSupport")}
                      </Link>
                    )}
                  </Stack>
                </Wrapper>
              </Fade>
            )}
          </Popper>
        </ClickAwayListener>
      )}
    </>
  );
};

export default HelpPopup;

const Wrapper = styled(Box, { shouldForwardProp: (propName) => propName !== "visible" })<{ visible: boolean }>(
  ({ theme, visible }) => ({
    opacity: 1,
    // position: "absolute",
    // bottom: theme.spacing(-2.5),
    // left: "100%",
    position: "relative",
    marginBottom: theme.spacing(-2.5),
    marginLeft: theme.spacing(1),
    width: 315,
    borderRadius: "12px",
    background: "#303049",
    color: theme.palette.common.white,
    padding: `${theme.spacing(2)} ${theme.spacing(2.5)}`,
    whiteSpace: "pre-wrap",
    transition: theme.transitions.create(["opacity", "transform"], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }),
    "&::after": {
      content: "''",
      position: "absolute",
      bottom: theme.spacing(4.5),
      left: -8,
      margin: "auto",
      display: "inline-block",
      height: 0,
      width: 0,
      borderTop: "8px solid transparent",
      //   borderRight: `8px solid ${theme.palette.primary.main}`,
      borderBottom: "8px solid transparent",
    },
  })
);
